<template>
  <div ref="componentEl">
    <aw-section-wrapper :has-padding="false">
      <template #content="{ contentEvents: { contentLoading } }">
        <aw-tab-carousel
          :main-title="title"
          :empty-placeholder="$awt('aw.shortlist.fallback.text')"
          :tabs="tabs"
          :breakpoints="breakpoints"
          :slides="slides"
          tab-key="id"
          slide-key="id"
          :class="[$style.tabCarousel]"
          :has-title-padding="hasTitlePadding"
          @tab-opened="selectShortlistTab(contentLoading, $event.newTabItem)"
          @render-sub-slides="onRender"
        >
          <template #slide(all)(all)="{ slide, tabItem }">
            <div
              :class="['awProductCardPlaceholder', $style.tabCarouselPlaceHolder]"
              :data-product-id="!slide.isPlaceholder ? slide.id : undefined"
              :data-product-index="!slide.isPlaceholder ? getGlobalProductIndex(slide, tabItem) : undefined"
            >
              <aw-product-card-version2
                v-if="!slide.isPlaceholder && productCardSize"
                :key="slide.id"
                :product-data="slide"
                :has-compare="false"
                :class="[$style.tabCarouselItem, $style.tabCarouselItemOffer]"
                :card-size="productCardSize"
                :opened-details="false"
                :bordered="isOnWhiteCard"
                heading-tag="h3"
                :list-index="getGlobalProductIndex(slide, tabItem)"
                :item-list-id="getItemListId(getGlobalProductIndex(slide, tabItem))"
                :item-list-name="getItemListName()"
              />
            </div>
          </template>
        </aw-tab-carousel>
        <div :class="$style.allProductsButtonWrapper">
          <lv-button
            styling="secondary-dark"
            shrink
            v-bind="{ to: getShortlistUrl({ identifier: selectedTabId, shType: 'id' }) }"
          >
            {{ $awt('aw.shortlist.show_all_products') }}
          </lv-button>
        </div>
      </template>
    </aw-section-wrapper>
  </div>
</template>

<script setup>
  import { defineAsyncComponent, nextTick } from 'vue';
  import { useNuxtApp } from 'nuxt/app';
  import { UnCachedProduct, updateProdWithUnCachedData, useProductsStore } from '~~/common/stores/products';
  import { getShortlistUrl } from '~~/common/composables/useUrls';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { getProductCardSizeV2 } from '~~/common/utils/productCard';
  import cmsPageComponentMixin from '~~/common/mixins/cmsPageComponentMixin.js';
  import analyticsBannerInteractionMixin from '~~/common/mixins/analyticsBannerInteractionMixin.js';
  import { LvButton } from '~~/common/components/loginet-vue-shop/index';
  import { useProductListAnalytics } from '~~/common/composables/useProductListAnalytics';

  const AwTabCarousel = defineAsyncComponent(() => import('~~/common/components/AwTabCarousel'));
  const AwProductCardVersion2 = defineAsyncComponent(() => import('~~/common/components/Common/AwProductCardVersion2'));
  const AwSectionWrapper = defineAsyncComponent(() => import('~~/common/components/Page/Common/AwSectionWrapper'));
  defineOptions({
    inheritAttrs: false,
  });
  const props = defineProps({
    ...analyticsBannerInteractionMixin.props,
    ...cmsPageComponentMixin.props,
    content: {
      type: Object,
      required: true,
    },
    breakpointsOverwrite: {
      type: Array,
      default: () => ([]),
    },
    title: {
      type: String,
      default: null,
    },
    hasTitlePadding: {
      type: Boolean,
      default: true,
    },
  });
  const $emit = defineEmits(['is-empty']);
  const {
    $awEvents,
    $logger,
  } = useNuxtApp();
  const productsStore = useProductsStore();
  const screenRange = computed(() => {
    return useUserInterfaceStore().mediaQueries;
  });
  const breakpoints = computed(() => {
    return props.breakpointsOverwrite.length ? props.breakpointsOverwrite : [
      {
        at: screenRange.value['desktop-large-min'],
        count: 5,
      },
      {
        at: screenRange.value['desktop-small-min'],
        count: 4,
      },
      {
        at: screenRange.value['tablet-min'],
        count: 3,
      },
      {
        at: true,
        count: 'auto',
      },
    ];
  });
  const tabs = computed(() => {
    return props.content?.multipleContents?.map((item) => {
      return {
        id: item?.contentId,
        tabTitle: item?.contentTitle,
      };
    }) || [];
  });
  const componentEl = ref(null);
  const slides = ref(Object.fromEntries(tabs.value.map(e => [e?.id, []])));
  const selectedTabId = ref(null);
  const productCardSize = ref(null);
  let idThrottledSetCardSize = null;

  const tabName = computed(() => tabs.value.find(tab => tab.id === selectedTabId.value)?.tabTitle || null);

  const {
    getItemListId,
    getItemListName,
  } = useProductListAnalytics({
    componentEl,
    products: slides,
    analyticsBannerInteraction: props.analyticsBannerInteraction,
    itemListName: tabName,
    getProductIndex: (product) => {
      const tabId = selectedTabId.value;
      if (!tabId || !slides.value[tabId]) {
        return 0;
      }
      const productList = slides.value[tabId];
      const index = productList.findIndex(p => p?.id === product?.id);
      return index !== -1 ? index : 0;
    },
  });
  watch(() => tabs.value.length, (newVal) => {
    if (Object.keys(slides.value).length !== newVal) {
      initSlides();
    }
  });

  onMounted(() => {
    $awEvents.addEvent('throttled-resize', setCardSize);
    initSlides();
  });
  onUnmounted(() => {
    $awEvents.removeEvent('throttled-resize', setCardSize);
  });

  async function onRender () {
    if (!productCardSize.value) {
      await nextTick();
      await nextTick();
      if (!productCardSize.value) {
        setCardSize();
      }
    }
  }

  function setCardSize () {
    const rAF = (cb) => {
      if (import.meta.client) {
        return requestAnimationFrame(cb);
      } else {
        cb();
        return null;
      }
    };
    const cAF = import.meta.client ? cancelAnimationFrame : (() => {
    });
    cAF(idThrottledSetCardSize);
    idThrottledSetCardSize = rAF(() => {
      const placeholder = (componentEl.value
        ?.querySelector?.('.awProductCardPlaceholder')
      );
      const clientWidth = placeholder?.clientWidth;
      const pcs = getProductCardSizeV2(clientWidth);
      cAF(idThrottledSetCardSize);
      idThrottledSetCardSize = rAF(() => {
        if (pcs) {
          productCardSize.value = pcs;
        }
      });
    });
  }

  function initSlides () {
    const placeholders = Array(breakpoints.value.find(b => b.at).count).fill();
    tabs.value.forEach((tab) => {
      slides.value[tab.id] = placeholders.map((_u, idx) => ({
        id: idx,
        isPlaceholder: true,
      }));
    });
  }

  // show alcohol popup if shortlist is contains containsAgeRestrictedProduct flag
  async function selectShortlistTab (updateLoadingState, { id }) {
    selectedTabId.value = id;
    if (slides.value[id]?.length && !slides.value[id][0]?.isPlaceholder) {
      return;
    }
    const params = {
      isCached: true,
      listId: id,
      page: 1,
      itemsPerPage: 12,
      filterParams: {
        availableAndOffline: 1,
      },
    };

    if (id) {
      if (import.meta.server) {
        // NOTE: AUCHSUP-6691 Aborts here because SSR won't wait for async event
        // handler to finish.
        return;
      }
      const {
        value: data,
        isCached,
      } = await productsStore.fetchCachedProductOrFallback({ params });
      slides.value[id] = data?.results;
      if (isCached) {
        const resp = await productsStore.getUnCachedPartsReq({ productIds: (slides.value[id] || []).map(p => p.id) });
        if (resp?.list && Object.keys(resp.list).length) {
          for (const k in resp.list) {
            resp.list[k] = new UnCachedProduct(resp.list[k]);
          }
          updateProdWithUnCachedData({
            productsToMutate: slides.value[id],
            payload: resp.list,
            $logger,
          });
        }
      }
    } else {
      slides.value[id] = [];
    }

    updateLoadingState(false);

    if (slides.value[id]) {
      $emit('is-empty', slides.value[id].length === 0);
    } else {
      $emit('is-empty', true);
    }

  }

  function getGlobalProductIndex (slide, tabItem) {
    const tabId = tabItem?.id;
    if (!tabId || !slides.value[tabId]) {
      return 0;
    }

    const productList = slides.value[tabId];
    const index = productList.findIndex(p => p?.id === slide?.id);

    return index !== -1 ? index : 0;
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.tabCarousel {
  --awTabItemPadding: 14px 16px;

  @include tablet(min) {
    --awTabItemPadding: 12px 16px;
  }

  &PlaceHolder {
    margin-top: 2px;
  }

  &Item {
    min-width: 160px !important; // AwProductCard override

    @include mobile(max) {
      width: 200px;
      max-width: 200px;
    }
  }
}

.allProductsButtonWrapper {
  display: flex;
  justify-content: center;
  margin-top: 20px;

  @include tablet(min) {
    margin-top: 40px;
  }
}
</style>
